// Dates are stored as EXCEL SERIAL NUMBERS, not as strings or Date objects.
//
// Verified against the bundled parser: `DATE(2026,3,1)` is 46082, `YEAR(46082)`
// is 2026, `EOMONTH`/`EDATE`/`TEXT` all agree. Storing a serial is therefore
// the only representation where the 280 built-in functions work on a date the
// user typed — a string would make every one of them fail, and a Date object
// would not survive JSON.
//
// Parsing is LOCALE-AWARE and refuses to guess: `3/4/2026` means different days
// in Sydney and San Francisco, so the day/month order comes from the browser's
// own formatter rather than a hardcoded convention.

/** Excel's day 0. (Its 1900 leap-year bug is baked into this offset.) */
const EPOCH_MS = Date.UTC(1899, 11, 30);
const DAY_MS = 86_400_000;

/** Anything outside this is far more likely to be a plain number than a date. */
const MIN_SERIAL = 1;
const MAX_SERIAL = 2_958_465; // 9999-12-31

export function serialToDate(serial: number): Date {
  return new Date(EPOCH_MS + Math.round(serial) * DAY_MS);
}

export function dateToSerial(date: Date): number {
  return Math.round((Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - EPOCH_MS) / DAY_MS);
}

export function ymdToSerial(y: number, m: number, d: number): number {
  return Math.round((Date.UTC(y, m - 1, d) - EPOCH_MS) / DAY_MS);
}

export function serialParts(serial: number): { y: number; m: number; d: number } {
  const date = serialToDate(serial);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
}

export function isPlausibleSerial(n: number): boolean {
  return Number.isFinite(n) && n >= MIN_SERIAL && n <= MAX_SERIAL;
}

// ---------------------------------------------------------------------------
// Locale
// ---------------------------------------------------------------------------

let cachedOrder: 'dmy' | 'mdy' | 'ymd' | null = null;

/**
 * Does this locale write the day or the month first? Read off Intl rather than
 * assumed — getting it backwards silently turns the 3rd of April into the 4th
 * of March, which is the kind of wrong nobody notices until a cashflow is late.
 */
export function localeDateOrder(): 'dmy' | 'mdy' | 'ymd' {
  if (cachedOrder) return cachedOrder;
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date(Date.UTC(2026, 2, 4)));
    const order = parts.filter((p) => p.type === 'day' || p.type === 'month' || p.type === 'year').map((p) => p.type);
    if (order[0] === 'year') cachedOrder = 'ymd';
    else cachedOrder = order[0] === 'day' ? 'dmy' : 'mdy';
  } catch {
    cachedOrder = 'mdy';
  }
  return cachedOrder;
}

const MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

function monthFromName(name: string): number | null {
  const i = MONTHS.indexOf(name.slice(0, 3).toLowerCase());
  return i < 0 ? null : i + 1;
}

function valid(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1000 || y > 9999) return false;
  const date = new Date(Date.UTC(y, m - 1, d));
  // Rejects 31 February rather than rolling it into March.
  return date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/**
 * Parse typed text as a date, returning an Excel serial. Returns null for
 * anything that is not clearly a date — a bare number, an address, a code.
 */
export function parseDate(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;

  // ISO — unambiguous in every locale, so it is tried first.
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (iso) {
    const [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
    return valid(y, m, d) ? ymdToSerial(y, m, d) : null;
  }

  // `1 Mar 2026`, `1 March 2026`, `Mar 1 2026`, `Mar 2026`.
  const named = /^(?:(\d{1,2})\s+)?([A-Za-z]{3,9})\.?\s+(?:(\d{1,2}),?\s+)?(\d{4})$/.exec(t);
  if (named) {
    const m = monthFromName(named[2]!);
    if (m !== null) {
      const d = Number(named[1] ?? named[3] ?? 1);
      const y = Number(named[4]);
      if (valid(y, m, d)) return ymdToSerial(y, m, d);
    }
  }

  // Numeric, separated by / . or -. Two digits of year are NOT accepted:
  // "3/4/26" is as likely to be a ratio or a score as a date.
  const numeric = /^(\d{1,4})[/.-](\d{1,2})[/.-](\d{2,4})$/.exec(t);
  if (numeric) {
    const a = Number(numeric[1]);
    const b = Number(numeric[2]);
    const c = Number(numeric[3]);
    if (String(numeric[3]).length !== 4) return null;

    // A day past 12 settles the order on its own, whatever the locale says.
    const candidates: [number, number, number][] =
      a > 1000
        ? [[a, b, c]]
        : a > 12
          ? [[c, b, a]]
          : b > 12
            ? [[c, a, b]]
            : localeDateOrder() === 'mdy'
              ? [[c, a, b]]
              : [[c, b, a]];
    for (const [y, m, d] of candidates) if (valid(y, m, d)) return ymdToSerial(y, m, d);
  }

  return null;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function df(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(undefined, { ...options, timeZone: 'UTC' });
    fmtCache.set(key, f);
  }
  return f;
}

export type DateStyle = 'date' | 'dateLong' | 'monthYear';

export function formatSerial(serial: number, style: DateStyle): string {
  if (!isPlausibleSerial(serial)) return String(serial);
  const date = serialToDate(serial);
  switch (style) {
    case 'dateLong':
      return df({ day: 'numeric', month: 'short', year: 'numeric' }).format(date);
    case 'monthYear':
      return df({ month: 'short', year: 'numeric' }).format(date);
    case 'date':
    default:
      return df({ day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }
}

/** Add whole months, clamping to the end of a short month (31 Jan + 1 = 28 Feb). */
export function addMonths(serial: number, months: number): number {
  const { y, m, d } = serialParts(serial);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  return ymdToSerial(target.getUTCFullYear(), target.getUTCMonth() + 1, Math.min(d, lastDay));
}

/** Whole months between two serials, or null when they are not a whole number apart. */
export function monthsBetween(a: number, b: number): number | null {
  const pa = serialParts(a);
  const pb = serialParts(b);
  const months = (pb.y - pa.y) * 12 + (pb.m - pa.m);
  return addMonths(a, months) === b ? months : null;
}
